// EL ÚNICO lugar de la app donde se dibuja el HTML de una respuesta gráfica.
//
// Es a propósito un componente diminuto: toda la seguridad está en las
// constantes que importa (`SANDBOX_GRAFICO`, y la CSP que
// `construirDocumentoGrafico` mete adentro del documento), no en este archivo.
// Así el `documentoGrafico.test.ts` puede afirmar sobre ellas sin DOM, y el
// diff de cualquier cambio de aislamiento se ve en un solo lugar.
//
// ⚠️  LEER `documentoGrafico.ts` ANTES DE TOCAR NADA DE ACÁ.
//     El HTML lo escribe un MODELO. Si mañana alguien "simplifica" esto a un
//     `dangerouslySetInnerHTML`, o le suma `allow-same-origin` al sandbox
//     "para que ande X", el documento pasa a correr en el origen de Atlas y
//     puede leer el token de sesión de Supabase del `localStorage` y mandarlo
//     afuera. No hay ninguna funcionalidad que justifique eso.
//
// LO ÚNICO que este componente hace además de dibujar es contar las cargas del
// marco (`onLoad`), y es una defensa, no un adorno: ver `alCargar`.

import { useRef, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { construirDocumentoGrafico, SANDBOX_GRAFICO } from '../documentoGrafico';

interface Props {
  /** HTML crudo del modelo. NO es de confianza; no lo toques fuera del iframe. */
  html: string;
  /** Título del gráfico: va al `title` del iframe (lo lee el lector de pantalla). */
  titulo: string;
  className?: string;
}

export const VisorGrafico: React.FC<Props> = ({ html, titulo, className }) => {
  // Cuántas veces cargó ESTE marco. El `srcdoc` cuenta una; cualquier otra es
  // una navegación que el documento se hizo solo.
  const cargasRef = useRef(0);
  const [navegado, setNavegado] = useState(false);
  // `intento` sólo existe para remontar el iframe cuando el usuario decide
  // volver a mostrarlo después de un aviso.
  const [intento, setIntento] = useState(0);

  // El contador se pone en cero DURANTE EL RENDER y no en un `useEffect`: el
  // evento `load` del iframe puede llegar antes de que corran los efectos, y
  // ahí un reset tardío se comería la primera carga — o sea que la navegación
  // siguiente pasaría por "la carga del srcdoc" y no se detectaría.
  const dibujadoRef = useRef<{ html: string; intento: number } | null>(null);
  if (dibujadoRef.current?.html !== html || dibujadoRef.current?.intento !== intento) {
    dibujadoRef.current = { html, intento };
    cargasRef.current = 0;
    if (navegado) setNavegado(false);
  }

  /**
   * LA ÚNICA DEFENSA QUE QUEDA CONTRA UNA NAVEGACIÓN DEL PROPIO MARCO.
   *
   * Ninguna CSP frena que el documento se navegue a sí mismo (`location.href`,
   * o un `<a>` sin `target`), y el documento remoto que llega después ya no
   * lleva nuestra `<meta>`: tiene red completa. Sigue en origen opaco —no ve el
   * token ni la ventana de arriba— pero puede pintarse como Atlas y pedirle al
   * usuario la contraseña, adentro de un panel con nuestro header y con nuestro
   * dominio en la barra de direcciones. Ver el bloque de `documentoGrafico.ts`.
   *
   * El evento `load` del ELEMENTO iframe dispara también en navegaciones
   * cross-origin (el padre no puede leer NADA del documento nuevo, pero sí se
   * entera de que hubo una carga). Así que: la primera es el `srcdoc`, y de la
   * segunda en adelante tiramos el marco abajo y mostramos un aviso.
   */
  const alCargar = () => {
    cargasRef.current += 1;
    if (cargasRef.current > 1) setNavegado(true);
  };

  if (navegado) {
    return (
      <div
        className={`flex h-full w-full flex-col items-center justify-center gap-3 bg-white px-6 text-center ${className ?? ''}`}
      >
        <ShieldAlert className="h-7 w-7 text-[#8A5A08]" aria-hidden="true" />
        <p className="text-[14px] font-semibold text-brand-black">
          Esta respuesta gráfica se fue a otra página
        </p>
        <p className="max-w-[46ch] text-[13px] leading-snug text-[#02062B]/[0.55]">
          El gráfico intentó salir solo del marco, así que lo cerramos. No
          escribas datos tuyos en algo que aparezca acá adentro: Atlas nunca te
          va a pedir la contraseña dentro de un gráfico.
        </p>
        <button
          type="button"
          onClick={() => setIntento((n) => n + 1)}
          className="rounded-full border border-[#02062B]/[0.12] bg-white py-2 px-4 text-[13px] font-medium text-brand-black transition-colors hover:border-[#1644EB]/[0.35] hover:text-brand-primary"
        >
          Mostrarlo de nuevo
        </button>
      </div>
    );
  }

  return (
    <iframe
      // Remontar de cero cuando el usuario pide volver a mostrarlo: el
      // documento viejo (y sus timers) se van con el elemento.
      key={intento}
      // `srcDoc` y no `src`: el documento viaja inline y el marco queda en un
      // ORIGEN OPACO ("null"). Sin origen no hay `localStorage`, ni cookies, ni
      // forma de leer nada de la ventana de arriba.
      srcDoc={construirDocumentoGrafico(html)}
      // El único token es `allow-scripts`. NUNCA `allow-same-origin` (las dos
      // juntas anulan el sandbox entero y el marco puede sacarse el atributo
      // solo). Sin `allow-top-navigation` no se lleva la pestaña a otro lado,
      // sin `allow-popups` no abre ventanas, sin `allow-forms` no manda un
      // `<form>` a ningún servidor y sin `allow-modals` no tapa la pantalla con
      // un `alert()`.
      sandbox={SANDBOX_GRAFICO}
      onLoad={alCargar}
      // Nada de permisos delegados (cámara, micrófono, geolocalización…). Con el
      // origen opaco ya estarían denegados, pero dejarlo escrito evita que el
      // día que alguien agregue `allow-same-origin` esto además se abra solo.
      allow=""
      referrerPolicy="no-referrer"
      title={titulo}
      // El documento trae su propio scroll y su propio padding: el iframe ocupa
      // todo el alto que le den y el contenido largo scrollea ADENTRO. Medirlo
      // desde afuera es imposible sin `allow-same-origin`, y no vale la pena
      // pagar eso por un autoajuste de alto.
      className={`h-full w-full border-0 bg-white ${className ?? ''}`}
    />
  );
};

export default VisorGrafico;
